import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router-dom'
import styled, {keyframes} from 'styled-components'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import MuiTextField from '@material-ui/core/TextField'
import Button from 'lib/ui/Button'
import Dialog from 'lib/ui/Dialog'
import NameCompletionDialog from 'organization/Obie/Blocks/ProcessForm/NameCompletionDialog'
import TypingEffect from 'organization/Obie/Blocks/ProcessForm/TypeEffect'
import {profileRoute} from 'organization/Obie/ObieRoutes'
import {pagRoute, sessionRoute} from 'organization/Obie/PagRoutes'
import {
  AWAITING_MESSAGES,
  Completion,
  splitText,
  useObieService,
} from 'organization/Obie/ObieServiceProvider'
import {useOrganization} from 'organization/OrganizationProvider'

export default function Complete(props: {
  category_id: string
  dependencies?: string
  completion?: Completion
}) {
  const {category_id, dependencies, completion} = props
  const {routes} = useOrganization()
  const history = useHistory()
  const {
    awaitingCompletion,
    fetchBlocks,
    findBlockById,
    profileId,
    regenerateCompletion,
    setAwaitingCompletion,
    setCompletion,
  } = useObieService()

  const [allTyped, setAllTyped] = useState(false)
  const [nameDialogOpen, setNameDialogOpen] = useState<boolean>(false)
  const [localCompletion, setLocalCompletion] = useState<Completion | null>(
    null,
  )
  const [completionText, setCompletionText] = useState<string>('')
  const [
    regenerateInstructionsOpen,
    setRegenerateInstructionsOpen,
  ] = useState<boolean>(false)
  const [isPagRedirect, setIsPagRedirect] = useState<boolean>(false)
  const [isSessionRedirect, setIsSessionRedirect] = useState<boolean>(false)

  const awaitingMessageRef = useRef<string>('')

  // We need some local details when we actually have a completion from the
  // ObieServiceProvider
  useEffect(() => {
    if (!completion) {
      return
    }

    if (
      completion.answer_set_id !== undefined &&
      completion.sessions !== undefined
    ) {
      setIsPagRedirect(true)
      fetchBlocks(parseInt(category_id), profileId)
      return
    }

    // If there's a day_index and session_index associated with the Completion, we
    // need to send user to the PAG Builder UI, they just did a completion for one
    // of the sessions.
    if (
      completion.day_index !== undefined &&
      completion.session_index !== undefined
    ) {
      setIsSessionRedirect(true)
      return
    }

    const splitTextParts = splitText(completion.completion)

    setLocalCompletion(completion)
    setCompletionText(splitTextParts[1] ?? splitTextParts[0])
    setAllTyped(false)
  }, [
    category_id,
    completion,
    fetchBlocks,
    profileId,
    setLocalCompletion,
    setCompletionText,
  ])

  useEffect(() => {
    // When isSessionRedirect is empty or (for whatever reason) there isn't any
    // Completion available, we can't redirect.
    if (!isPagRedirect || !completion) {
      return
    }

    const pRoute = pagRoute(routes, Number(completion.id), Number(profileId))

    history.push(pRoute)
  }, [completion, isPagRedirect, history, profileId, routes])

  useEffect(() => {
    // When isSessionRedirect is empty or (for whatever reason) there isn't any
    // Completion available, we can't redirect.
    if (!isSessionRedirect || !completion) {
      return
    }

    const sRoute = sessionRoute(
      routes,
      Number(completion.pag_id),
      Number(completion.profile_id),
      Number(completion.day_index),
      Number(completion.session_index),
    )

    history.push(sRoute.root)
  }, [completion, isSessionRedirect, history, routes])

  const regenerate = useCallback(
    (extraInstructions: string) => {
      if (!localCompletion) {
        return
      }

      setRegenerateInstructionsOpen(false)
      setAwaitingCompletion(true)
      setLocalCompletion(null)
      awaitingMessageRef.current = ''

      regenerateCompletion(
        localCompletion.id,
        dependencies,
        extraInstructions,
      ).then((response) => {
        setCompletion(response)
        setAwaitingCompletion(false)
      })
    },
    [
      localCompletion,
      dependencies,
      regenerateCompletion,
      setAwaitingCompletion,
      setCompletion,
    ],
  )

  const toggleNameCompletionDialog = () =>
    setNameDialogOpen((current) => !current)

  const viewCompletions = () => {
    const pRoute = profileRoute(routes, String(category_id), String(profileId))

    history.push(pRoute.root)
  }

  const preRegenerate = () => setRegenerateInstructionsOpen(true)
  const closePreGenerate = () => setRegenerateInstructionsOpen(false)

  // If we're waiting for a Completion to come back from chat, we want to render
  // a "waiting" message to user. There are a list of them, which we randomly
  // get one from.
  if (awaitingCompletion) {
    // ref because this component renders a couple times through it's lifecycle,
    // we don't want to re-render a differnet "waiting" message through the life.
    if (!awaitingMessageRef.current) {
      awaitingMessageRef.current =
        AWAITING_MESSAGES[Math.floor(Math.random() * AWAITING_MESSAGES.length)]
    }

    return (
      <Container>
        <Content>
          <StyledTypingEffect text={awaitingMessageRef.current} />
          <Blinky />
        </Content>
      </Container>
    )
  }

  // If there isn't a completion available and we're not waiting for one, we
  // don't want to render anything... We also want to bail if we don't have a
  // profileId in state.
  if (!localCompletion || profileId === undefined) {
    return null
  }

  const block = findBlockById(localCompletion.block_id)

  const handleAllTyped = () => setAllTyped(true)

  return (
    <Container>
      <RegenerateInstructions
        open={regenerateInstructionsOpen}
        onClose={closePreGenerate}
        regenerate={regenerate}
      />

      <Content>
        <NameCompletionDialog
          completion={localCompletion}
          completionText={completionText}
          onClose={toggleNameCompletionDialog}
          viewCompletions={viewCompletions}
          open={nameDialogOpen}
        />
        <TypingEffect
          text={localCompletion.completion}
          onFinish={handleAllTyped}
          updateCompletion={setCompletionText}
        />
        <ButtonBox>
          <SaveCompletionButton
            type="button"
            variant="contained"
            color="primary"
            showing={allTyped}
            onClick={toggleNameCompletionDialog}
          >
            Save {block?.block || 'Block'}
          </SaveCompletionButton>
          <RegenerateCompletionButton
            type="button"
            variant="contained"
            color="primary"
            showing={allTyped}
            onClick={preRegenerate}
          >
            Regenerate
          </RegenerateCompletionButton>
        </ButtonBox>
      </Content>
    </Container>
  )
}

const RegenerateInstructions = (props: {
  open: boolean
  onClose: () => void
  regenerate: (regenerateInstructions: string) => void
}) => {
  const {onClose, open, regenerate} = props
  const [regenerateInstructions, setRegenerateInstructions] = useState<string>(
    '',
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setRegenerateInstructions(event.target.value)

  const handleClick = () => regenerate(regenerateInstructions)

  return (
    <Dialog expandable={false} open={open} onClose={onClose} maxWidth="md">
      <DialogTitle>
        Are there any specific instructions you'd like me to know before I try
        again?
      </DialogTitle>
      <DialogContent>
        <MuiTextField
          value={regenerateInstructions}
          fullWidth
          multiline
          onChange={handleChange}
          rows={12}
          variant="filled"
        />

        <RegenerateButtonBox>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleClick}
          >
            Regenerate
          </Button>
        </RegenerateButtonBox>
      </DialogContent>
    </Dialog>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const Content = styled.div`
  position: relative;
  display: block;
  padding-left: ${(props) => props.theme.spacing[30]};
  padding-right: ${(props) => props.theme.spacing[30]};
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding-left: ${(props) => props.theme.spacing[6]};
    padding-right: ${(props) => props.theme.spacing[6]};
  }
`

const ButtonBox = styled.div`
  display: flex;
  margin: ${(props) => props.theme.spacing[8]} 0;
  padding-bottom: ${(props) => props.theme.spacing[8]};
  justify-content: center;
`
const RegenerateButtonBox = styled.div`
  display: flex;
  margin: ${(props) => props.theme.spacing[8]} 0;
  justify-content: center;

  & > button:first-child {
    margin-right: ${(props) => props.theme.spacing[8]};
  }
`

const SaveCompletionButton = styled(Button)<{showing?: boolean}>`
  display: ${(props) => (props.showing ? 'block' : 'none')};
  margin-right: ${(props) => props.theme.spacing[4]};
`

const RegenerateCompletionButton = styled(Button)<{showing?: boolean}>`
  display: ${(props) => (props.showing ? 'block' : 'none')};
  margin-left: ${(props) => props.theme.spacing[4]};
`

const StyledTypingEffect = styled(TypingEffect)`
  display: inline-block;
  padding-right: 2px;
`
const blinker = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`
const Blinky = styled.div`
  display: inline;
  width: 1px;
  height: 20px;
  border-left: solid 2px #fff;
  animation: ${blinker} 0.8s infinite;
`
